// adminfrontend/src/admin_ui/pages/AdminBranchesPage.jsx
import React, { useEffect, useState } from "react";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function AdminBranchesPage({ adminEmail, onLogout }) {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [form, setForm] = useState({ name: "", code: "" });

  async function load() {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const data = await adminInventoryApi.listBranches();
      if (!data || data.ok !== true) {
        setError("Failed to load branches.");
        setBranches([]);
        return;
      }
      setBranches(data.branches || []);
    } catch (e) {
      console.error("[AdminBranchesPage] load error:", e);
      setError("Unable to load branches. Check login or server.");
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setActionLoading(true);
    setError("");
    setInfo("");
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || undefined,
      };
      const res = await adminInventoryApi.createBranch(payload);
      if (!res || res.ok !== true) {
        setError("Failed to create branch.");
        return;
      }
      setInfo("Branch created successfully.");
      setForm({ name: "", code: "" });
      await load();
    } catch (e) {
      console.error("[AdminBranchesPage] create error:", e);
      setError("Unable to create branch (check name/code uniqueness).");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Branches</h1>
            <p className="ucx-admin-subtitle">
              Create and manage branches for inventory and stock tracking.
            </p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={load}
                disabled={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button
                type="button"
                className="ucx-admin-btn ucx-admin-btn-reject"
                onClick={onLogout}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Create Branch</div>

          <form className="ucx-admin-form" onSubmit={onSubmit}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-2">
              <div className="ucx-admin-field">
                <label>Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Gaborone HQ"
                  minLength={2}
                  required
                />
              </div>

              <div className="ucx-admin-field">
                <label>Code (optional)</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                  placeholder="e.g. GBE-HQ"
                />
              </div>
            </div>

            <div className="ucx-admin-form-actions">
              <button
                type="submit"
                className="ucx-admin-btn ucx-admin-btn-approve"
                disabled={actionLoading}
              >
                {actionLoading ? "Working…" : "Create Branch"}
              </button>
            </div>
          </form>

          {!!error && <div className="ucx-admin-status-error">{error}</div>}
          {!!info && <div className="ucx-admin-status-success">{info}</div>}
        </div>

        <div className="ucx-admin-status">
          {!loading && !error && branches.length === 0 && (
            <div className="ucx-admin-empty">
              No branches found. Create one to begin.
            </div>
          )}
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id}>
                  <td>{b.id}</td>
                  <td>{b.name}</td>
                  <td>{b.code || "-"}</td>
                  <td>{b.status || "-"}</td>
                  <td>{b.created_at ? new Date(b.created_at).toLocaleString() : "-"}</td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={5} className="ucx-admin-empty">
                    Loading branches…
                  </td>
                </tr>
              )}

              {!loading && branches.length === 0 && (
                <tr>
                  <td colSpan={5} className="ucx-admin-empty">
                    No branches yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default AdminBranchesPage;
